import Konva from 'konva/konva'
import Tooltip from './tooltip'
import { timeToX, xToTime } from '../utils'
import { translateVariableValue, translateVariableName } from '../../helpers/translation'
import moment from 'moment'

const color = 'Red'
const width = 1
const opacity = 1
const debounceDelay = 25
const verticalLineTimeFormat = 'D/M - HH:mm:ss.SSS z'
const durationStrip = { color: '#1976d220' }

export default class VerticalLine extends Konva.Layer {
  constructor ({ name }) {
    super({ name })
  }

  init ({ container, width, yFrom, yTo, timeFrom, timeTo, now, xScale, variables }) {
    Object.assign(this,
      { container, width, yFrom, yTo, timeFrom, timeTo, now, xScale, variables })
    this.fixedTime = null
    this.nowX = timeToX({ timeFrom, timeTo, width, time: now })

    this._cleanUp()

    container.onmousemove = this._debouncedRender.bind(this)

    this._resetDuration()
  }

  setFixedTime (fixedTime) {
    this.fixedTime = fixedTime
    if (fixedTime !== null) {
      this.time = fixedTime
    }
    this._render()
  }

  _cleanUp () {
    this.destroyChildren()
    this.draw()
  }

  _calculateTime () {
    const { width, timeFrom, timeTo, x, fixedTime } = this
    if (fixedTime === null) {
      this.time = xToTime({ timeFrom, timeTo, width, x })
    }
  }

  _calculateX () {
    const { width, timeFrom, timeTo, fixedTime } = this
    if (fixedTime !== null) {
      this.x = timeToX({ timeFrom, timeTo, width, time: fixedTime })
    }
  }

  _debouncedRender (event) {
    const { nowX, width, timeout } = this
    const parentPosition = this.getParent().getPointerPosition()
    if (parentPosition) {
      const { x, y } = parentPosition
      // at this time, there should be a pointer position; later in render() might not be there
      // console.log('mouse', {x, y})

      if (x > nowX || x > width) { // No vertical line in the future or on Y scale
        return
      }

      Object.assign(this, { x, y })

      if (timeout) {
        window.clearTimeout(timeout)
      }
      this.timeout = window.setTimeout(this._render.bind(this), debounceDelay)
    }
  }

  _render () {
    this.destroyChildren()

    this._calculateTime()
    this._calculateX()

    this._renderDurationStrip()
    this._renderVerticalLine()
    this._renderCompleteDurationTooltip()
    this._renderXScaleTooltip()
    this._renderVariablesTooltips()

    this.draw()
  }

  _renderVerticalLine () {
    const { x, yFrom, yTo } = this
    this.add(new Konva.Line({
      points: [x, yFrom, x, yTo],
      stroke: color,
      strokeWidth: width,
      opacity
    }))
  }

  _renderXScaleTooltip () {
    const { width, xScale, x, time } = this
    this.add(new Tooltip({
      x,
      y: xScale.yOffset + xScale.midHeight,
      width,
      text: xScale.getLabelAtTime(time)
    }))
  }

  _renderVariablesTooltips () {
    const { variables, y } = this
    const hoveredOnly = variable => {
      const { yOffset, height } = variable
      return y >= yOffset && y < yOffset + height
    }
    variables
      .filter(hoveredOnly)
      .forEach(variable => this._renderVariableTooltip(variable))
  }

  _renderVariableTooltip (variable) {
    const { width, x, time, durationStart, durationLabel } = this
    const { yOffset, midHeight } = variable
    let label = variable.getLabelAtTime(time)
    if (label === undefined || label === null) { // Keep 0 as a valid value
      label = ''
    }
    const text = durationStart && !durationLabel
      ? this.calculateDuration(x)
      : translateVariableName(variable.name) + ': ' + translateVariableValue(label) +
      ' - [' + moment(time).format(verticalLineTimeFormat) + ']'
    if (text) {
      this.add(new Tooltip({ x, y: yOffset + midHeight, width, text }))
    }
  }

  _renderCompleteDurationTooltip () {
    const { width, durationLabel, durationLabelPosition } = this
    if (durationLabel) {
      const { x, y } = durationLabelPosition
      this.add(new Tooltip({ x, y, width, text: durationLabel }))
    }
  }

  _renderDurationStrip () {
    const { x, durationStart, durationEnd, yFrom, yTo } = this
    if (durationStart) {
      this.add(new Konva.Rect({
        x: durationStart,
        y: yFrom,
        width: (durationEnd || x) - durationStart,
        height: yTo - yFrom,
        fill: durationStrip.color
      }))
    }
  }

  _resetDuration () {
    this.durationStart = null
    this.durationEnd = null
    this.durationLabel = null
    this.durationLabelPosition = null
  }

  onClick (event) {
    const { ctrlKey } = event.evt
    const { durationStart, durationLabel, x, y } = this
    if (!ctrlKey) {
      this._resetDuration()
    } else {
      if (!durationStart) {
        this.durationStart = x
      } else if (!durationLabel) {
        this.durationEnd = x
        this.durationLabel = this.calculateDuration(x)
        this.durationLabelPosition = { x, y }
      } else {
        this._resetDuration()
      }
    }
    this._render()
  }

  calculateDuration (x) {
    const { width, timeFrom, timeTo, durationStart } = this
    const startTime = xToTime({ timeFrom, timeTo, width, x: durationStart })
    const endTime = xToTime({ timeFrom, timeTo, width, x })
    return moment.duration(Math.abs(endTime - startTime)).format()
  }
}
